"use client";

import Link from "next/link";
import {
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { PracticeToolIcon } from "../practice-tool-icon";
import { scoreboardTools } from "../scoreboards/catalog";
import { PlayerIcon } from "./icon";

type ServiceApp = {
  title: string;
  label: string;
  icon: ReactNode;
} & (
  | { href: string; picker?: never }
  | { picker: "scoreboards"; href?: never }
);

const sections: { id: string; title: string; apps: ServiceApp[] }[] = [
  {
    id: "practice",
    title: "練習",
    apps: [
      {
        title: "弱点登録",
        label: "弱点",
        href: "/weaknesses",
        icon: <PracticeToolIcon name="weaknesses" />,
      },
      {
        title: "ドリル",
        label: "ドリル",
        href: "/drills",
        icon: <PracticeToolIcon name="drills" />,
      },
      {
        title: "レッスン",
        label: "レッスン",
        href: "/lessons",
        icon: <PracticeToolIcon name="lessons" />,
      },
      {
        title: "カウントナイン",
        label: "カウントナイン",
        href: "/count-nine",
        icon: <PracticeToolIcon name="count-nine" />,
      },
      {
        title: "ボーラード",
        label: "ボーラード",
        href: "/bowlard",
        icon: <PracticeToolIcon name="bowlard" />,
      },
    ],
  },
  {
    id: "tools",
    title: "ツール",
    apps: [
      {
        title: "ブレイクスピードチェック",
        label: "スピード",
        href: "/break-speed",
        icon: <PracticeToolIcon name="break-speed" />,
      },
      {
        title: "テーブル配置図",
        label: "配置図",
        href: "/layouts",
        icon: <PracticeToolIcon name="layouts" />,
      },
      {
        title: "撞点図",
        label: "撞点図",
        href: "/cue-tip",
        icon: <PracticeToolIcon name="cue-tip" />,
      },
      {
        title: "スコアボード",
        label: "スコア",
        picker: "scoreboards",
        icon: <PracticeToolIcon name="set-match" />,
      },
    ],
  },
  {
    id: "discover",
    title: "探す",
    apps: [
      {
        title: "イベント",
        label: "イベント",
        href: "/events",
        icon: <PracticeToolIcon name="events" />,
      },
      {
        title: "施設",
        label: "施設",
        href: "/stores",
        icon: <PracticeToolIcon name="stores" />,
      },
      {
        title: "メンバー募集",
        label: "メンバー",
        href: "/recruitments",
        icon: <PracticeToolIcon name="people" />,
      },
      {
        title: "プレイヤー検索",
        label: "プレイヤー",
        href: "/players",
        icon: <PracticeToolIcon name="players" />,
      },
    ],
  },
  {
    id: "shop",
    title: "オンラインストア",
    apps: [
      {
        title: "オンラインストア",
        label: "ストア",
        href: "/shop",
        icon: <PracticeToolIcon name="shop" />,
      },
    ],
  },
];

export function AppLauncher() {
  const [open, setOpen] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const dismiss = useCallback(() => {
    setOpen(false);
    trigger.current?.focus({ preventScroll: true });
  }, []);
  return (
    <>
      <button
        ref={trigger}
        type="button"
        className="player-header-icon app-launcher-trigger"
        aria-label="サービス一覧"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? "app-launcher" : undefined}
        onClick={() => setOpen(true)}
      >
        <PlayerIcon name="apps" />
      </button>
      {open &&
        createPortal(<AppLauncherDialog onDismiss={dismiss} />, document.body)}
    </>
  );
}

function useLauncherDialog(onDismiss: () => void) {
  const dialog = useRef<HTMLDialogElement>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const dismiss = () => {
    dialog.current?.close();
    onDismiss();
  };
  useEffect(() => {
    const element = dialog.current;
    const overflow = document.body.style.overflow;
    element?.showModal();
    document.body.style.overflow = "hidden";
    heading.current?.focus({ preventScroll: true });
    return () => {
      element?.close();
      document.body.style.overflow = overflow;
    };
  }, []);
  return { dialog, heading, dismiss };
}

function LauncherDialog({
  id,
  title,
  dialog,
  heading,
  className = "",
  back = false,
  onDismiss,
  children,
}: {
  id: string;
  title: string;
  dialog: RefObject<HTMLDialogElement | null>;
  heading: RefObject<HTMLHeadingElement | null>;
  className?: string;
  back?: boolean;
  onDismiss: () => void;
  children: ReactNode;
}) {
  const action = (
    <button
      className="app-launcher-close"
      type="button"
      aria-label={back ? "サービス一覧に戻る" : `${title}を閉じる`}
      onClick={onDismiss}
    >
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <path d={back ? "m12 5-7 7 7 7M5 12h14" : "m6 6 12 12M6 18 18 6"} />
      </svg>
    </button>
  );
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Native dialog provides Escape dismissal through onCancel; clicking only dismisses the backdrop.
    <dialog
      ref={dialog}
      id={id}
      className={`app-launcher ${className}`}
      aria-labelledby={`${id}-title`}
      onCancel={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onDismiss();
      }}
      onClick={(event) => {
        if (event.target !== event.currentTarget) return;
        const bounds = event.currentTarget.getBoundingClientRect();
        if (
          event.clientX < bounds.left ||
          event.clientX > bounds.right ||
          event.clientY < bounds.top ||
          event.clientY > bounds.bottom
        ) {
          event.stopPropagation();
          onDismiss();
        }
      }}
    >
      <header className="app-launcher-heading">
        <h2 id={`${id}-title`} ref={heading} tabIndex={-1}>
          {title}
        </h2>
        {action}
      </header>
      <div className="app-launcher-sections">{children}</div>
    </dialog>
  );
}

function AppLauncherDialog({ onDismiss }: { onDismiss: () => void }) {
  const { dialog, heading, dismiss } = useLauncherDialog(onDismiss);
  const [scoreboardsOpen, setScoreboardsOpen] = useState(false);
  const scoreTrigger = useRef<HTMLButtonElement>(null);
  const serviceScrollTop = useRef(0);
  const previousView = useRef(false);
  const showScoreboards = () => {
    serviceScrollTop.current = dialog.current?.scrollTop ?? 0;
    setScoreboardsOpen(true);
  };
  const returnToServices = () => setScoreboardsOpen(false);
  useLayoutEffect(() => {
    if (scoreboardsOpen) {
      if (dialog.current) dialog.current.scrollTop = 0;
      heading.current?.focus({ preventScroll: true });
    } else if (previousView.current) {
      if (dialog.current) dialog.current.scrollTop = serviceScrollTop.current;
      scoreTrigger.current?.focus({ preventScroll: true });
    }
    previousView.current = scoreboardsOpen;
  }, [scoreboardsOpen, dialog, heading]);
  return (
    <LauncherDialog
      id="app-launcher"
      title={scoreboardsOpen ? "スコアボード" : "サービス一覧"}
      dialog={dialog}
      heading={heading}
      className={scoreboardsOpen ? "app-launcher-scoreboards" : ""}
      back={scoreboardsOpen}
      onDismiss={scoreboardsOpen ? returnToServices : dismiss}
    >
      {scoreboardsOpen ? (
        <ScoreboardChoices onSelect={dismiss} />
      ) : (
        sections.map((section) => (
          <section
            key={section.id}
            className="app-launcher-section"
            aria-labelledby={`app-launcher-${section.id}`}
          >
            <h3 id={`app-launcher-${section.id}`}>{section.title}</h3>
            <ul className="app-launcher-grid">
              {section.apps.map((app) => {
                const content = (
                  <>
                    <span className="app-launcher-symbol" aria-hidden="true">
                      {app.icon}
                    </span>
                    <span>{app.label}</span>
                  </>
                );
                return (
                  <li key={app.href ?? app.picker}>
                    {app.picker === "scoreboards" ? (
                      <button
                        ref={scoreTrigger}
                        type="button"
                        className="app-launcher-app"
                        aria-label={app.title}
                        onClick={showScoreboards}
                      >
                        {content}
                      </button>
                    ) : (
                      <Link
                        href={app.href}
                        className="app-launcher-app"
                        aria-label={app.title}
                        onClick={dismiss}
                      >
                        {content}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </LauncherDialog>
  );
}

const scoreboards = [
  { id: "set-match", title: "セットマッチ", href: "/set-match" },
  ...scoreboardTools,
];

function ScoreboardChoices({ onSelect }: { onSelect: () => void }) {
  return (
    <ul className="app-launcher-grid">
      {scoreboards.map((board) => (
        <li key={board.id}>
          <a href={board.href} className="app-launcher-app" onClick={onSelect}>
            <span className="app-launcher-symbol" aria-hidden="true">
              <PracticeToolIcon name="set-match" />
            </span>
            <span>{board.title}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}
